// The ordered superset of onboarding stages. The resolver filters this list by
// each stage's `appliesWhen` condition against the criteria, so Personal and
// Business yield different flows from one declarative table. Shared stages
// (account type, contact, review, submitted) omit `appliesWhen`.

import type { FlowStage } from '../schema.js';

const PERSONAL = { accountType: 'PERSONAL' as const };
const BUSINESS = { accountType: 'BUSINESS' as const };

export const defaultStages: FlowStage[] = [
  // Shared — account creation
  { id: 'account-type', screen: 'account-type', phase: 'account', label: 'Account type' },
  { id: 'contact', screen: 'contact', phase: 'account', label: 'Contact & sign-in' },

  // Personal — identity
  { id: 'details', screen: 'details', phase: 'identity', label: 'Your details', appliesWhen: PERSONAL },
  // Before the hosted identity check, not after: that check leaves our site for
  // the provider's own pages, and a step placed on the far side of a redirect
  // is one a returning applicant has to be walked back to.
  { id: 'personal-documents', screen: 'personal-documents', phase: 'identity', label: 'Proof of address', appliesWhen: PERSONAL },
  { id: 'identity-method', screen: 'identity-method', phase: 'identity', label: 'Verify your identity', appliesWhen: PERSONAL },
  { id: 'identity-capture', screen: 'identity-capture', phase: 'identity', label: 'Capture your document', appliesWhen: PERSONAL },

  // Business — the five sections of the corporate application, in the form's
  // own order:
  //   A general information (company, registered + trading address, contact)
  //   B business activity (activity, counterparties, profile)
  //   C financial information
  //   D directors and beneficial owners
  //   E documents
  // Each section was previously split across several screens, so an applicant
  // met ten steps for a five-section form and read that as being out of order.
  { id: 'section-company', screen: 'section-company', phase: 'identity', label: 'Company', appliesWhen: BUSINESS },
  { id: 'section-activity', screen: 'section-activity', phase: 'identity', label: 'Business activity', appliesWhen: BUSINESS },
  { id: 'section-financials', screen: 'section-financials', phase: 'identity', label: 'Financials', appliesWhen: BUSINESS },
  { id: 'section-people', screen: 'section-people', phase: 'identity', label: 'Directors & owners', appliesWhen: BUSINESS },
  { id: 'documents', screen: 'documents', phase: 'identity', label: 'Documents', appliesWhen: BUSINESS },

  // Shared — review & outcome
  { id: 'review', screen: 'review', phase: 'welcome', label: 'Review & finish' },
  { id: 'submitted', screen: 'submitted', phase: 'welcome', label: 'Submitted' },
];
